/**
 * Слова к наблюдению разбора периода — от модели, через сервер.
 *
 * Числа уже посчитаны ядром; сервер просит модель только сформулировать и
 * проверяет каждое число в ответе. Нет Pro, нет сети, модель ошиблась — `null`,
 * и карточка показывает текст по шаблону. Ответ запоминается на устройстве:
 * один вызов на наблюдение, а не на каждый показ «Обзора».
 */
import { useEffect, useState } from 'react';
import type { Observation, PeriodReview } from '@skoka/core';
import { api, unwrap } from './sync.js';

const STORE = 'skoka.reviewWords';
const keyOf = (r: PeriodReview, o: Observation) => `${r.period.key}:${o.categoryId}:${o.amount}:${o.target}`;

function cached(key: string): string | null | undefined {
  try {
    const all = JSON.parse(localStorage.getItem(STORE) ?? '{}') as Record<string, string | null>;
    return key in all ? all[key] : undefined;
  } catch { return undefined; }
}

function remember(key: string, text: string | null): void {
  try {
    const all = JSON.parse(localStorage.getItem(STORE) ?? '{}') as Record<string, string | null>;
    const keep = Object.fromEntries(Object.entries(all).slice(-20));
    localStorage.setItem(STORE, JSON.stringify({ ...keep, [key]: text }));
  } catch { /* без памяти — просто спросим снова */ }
}

const often = (o: Observation, r: PeriodReview): string => {
  const p = r.period;
  const days = (Date.parse(p.to ?? p.from) - Date.parse(p.from)) / 86_400_000 + 1;
  const per = days / Math.max(1, o.count);
  return per <= 1.3 ? 'почти каждый день' : per <= 2.5 ? 'почти через день' : '';
};

/** Текст модели или `null` — тогда шаблон. Спрашивает только при `enabled` (Pro). */
export function useReviewWords(review: PeriodReview | null, main: Observation | undefined, enabled: boolean): string | null {
  const key = review && main ? keyOf(review, main) : null;
  const [words, setWords] = useState<string | null>(() => (key ? cached(key) ?? null : null));
  useEffect(() => {
    if (!key || !review || !main || !enabled) { setWords(null); return; }
    const known = cached(key);
    if (known !== undefined) { setWords(known); return; }
    let live = true;
    void (async () => {
      try {
        const body = await unwrap(await api('/ai/period-review', {
          method: 'POST',
          body: JSON.stringify({ facts: {
            name: main.name, amount: main.amount, count: main.count, usual: main.usual, target: main.target,
            less: main.less, often: often(main, review),
            ...(main.goal ? { goal: { name: main.goal.name, months: main.goal.months } } : {}),
          } }),
        }));
        const text = typeof (body as { text?: unknown }).text === 'string' ? (body as { text: string }).text : null;
        remember(key, text);
        if (live) setWords(text);
      } catch {
        // Сеть или доступ — шаблон, без запоминания: в другой раз спросим снова.
      }
    })();
    return () => { live = false; };
  }, [key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return words;
}
